// project import
import Locales from 'components/Locales';
import ScrollTop from 'components/ScrollTop';
import Routes from 'routes';

// auth provider
import { AWSCognitoProvider as AuthProvider } from 'contexts/AWSCognitoContext';

// Utils
import ModalProvider from 'components/modals/ModalProvider';
import MonitoringUserProvider from 'components/MonitoringUserProvider';
import NotFoundRedirect from 'components/NotFoundRedirect';

import ApiStatusGuard from 'components/ApiStatusGuard';
import { HistoryTrail, HistoryTrailLabels } from 'components/HistoryTrail';
import { useDocumentTheme } from 'hooks/useUiTheme';
import { ConfigProvider } from 'contexts/ConfigContext';
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'components/icons';
import { CSSProperties, PropsWithChildren } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { Persistor } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import { Toaster } from 'sonner';
import { persister, store } from 'store';

// ==============================|| APP - ROUTER, LOCAL  ||============================== //

const MaybePersistGate = ({ children, persister }: PropsWithChildren<{ persister: Persistor }>): JSX.Element => {
    if (persister.getState().registry.length > 0) {
        return (
            <PersistGate loading={null} persistor={persister}>
                {children}
            </PersistGate>
        );
    }
    return <>{children}</>;
};

// Mounted above the auth provider, so it takes the theme the layout painted the page in, not the user's.
function Toasts() {
    const theme = useDocumentTheme();
    return (
        <Toaster
            theme={theme}
            position="top-center"
            richColors
            closeButton
            expand
            duration={6000}
            icons={{
                error: <CircleAlert />,
                info: <Info />,
                success: <CircleCheck />,
                warning: <TriangleAlert />,
            }}
            style={
                {
                    zIndex: 'var(--z-toast)',
                    '--width': '740px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                } as CSSProperties
            }
            toastOptions={{
                style: {
                    width: 'fit-content',
                    minWidth: '150px',
                },
            }}
        />
    );
}

const App = (): React.ReactElement => (
    <ReduxProvider store={store}>
        <MaybePersistGate persister={persister}>
            <ConfigProvider>
                <Locales>
                    <Toasts />
                    <ApiStatusGuard>
                        <ScrollTop>
                            <AuthProvider>
                                <MonitoringUserProvider>
                                    {/* The one tooltip group, modals included: the next tooltip opens at once. */}
                                    <TooltipPrimitive.Provider>
                                        <ModalProvider>
                                            <HistoryTrail />
                                            <HistoryTrailLabels />
                                            <NotFoundRedirect />
                                            <Routes />
                                        </ModalProvider>
                                    </TooltipPrimitive.Provider>
                                </MonitoringUserProvider>
                            </AuthProvider>
                        </ScrollTop>
                    </ApiStatusGuard>
                </Locales>
            </ConfigProvider>
        </MaybePersistGate>
    </ReduxProvider>
);

export default App;
